<template>
  <div class="chart-box">
    <div class="mode-row">
      <button
        v-for="m in ['gross', 'net']" :key="m" type="button"
        class="mode-btn" :class="{ active: mode === m }" @click="mode = m"
      >{{ m === 'gross' ? 'Gross salary' : 'Net · after tax' }}</button>
      <label class="au-input">
        AU top-of-scale (gross)
        <input
          type="number" min="90000" max="200000" step="1000"
          :value="auGross" @input="auGross = Number($event.target.value)"
        />
      </label>
    </div>

    <svg :viewBox="`0 0 ${W} ${H}`" class="chart">
      <!-- track to the AU value -->
      <rect :x="x(0)" :y="barY" :width="x(auValue) - x(0)" height="10" class="track" />
      <!-- PVIS today -->
      <rect :x="x(0)" :y="barY" :width="x(pvisValue) - x(0)" height="10" class="bar" />
      <!-- step markers -->
      <g v-for="m in stepMarkers" :key="m.label">
        <line :x1="x(m.value)" :x2="x(m.value)" :y1="barY - 8" :y2="barY + 18" class="step-marker" />
        <text :x="x(m.value)" :y="barY + 32" class="step-label" text-anchor="middle">{{ m.label }}</text>
      </g>
      <!-- AU benchmark -->
      <line :x1="x(auValue)" :x2="x(auValue)" :y1="barY - 12" :y2="barY + 22" class="au-marker" />
      <text :x="x(auValue)" :y="barY - 18" class="au-label" text-anchor="end">
        AU {{ modeLabel }} ≈ AUD {{ Math.round(auValue / 1000) }}k
      </text>
      <text :x="x(pvisValue) + 8" :y="barY - 18" class="pvis-label" text-anchor="start">
        CAT 1b ≈ AUD {{ Math.round(pvisValue / 1000) }}k
      </text>
      <text v-for="t in ticks" :key="t" :x="x(t)" :y="H - 6" class="tick" text-anchor="middle">{{ t / 1000 }}k</text>
    </svg>

    <div class="readout">
      <span class="big">{{ ((pvisValue / auValue) * 100).toFixed(0) }}%</span>
      of Australian parity on a {{ modeLabel }} basis
      <span class="sub">· with step 2 (+10%): {{ ((pvisValue * 1.1 / auValue) * 100).toFixed(0) }}%
      · with steps 2+3 (+21%): {{ ((pvisValue * 1.21 / auValue) * 100).toFixed(0) }}%</span>
    </div>
    <p class="note">
      Vanuatu levies no personal income tax, so a VUV salary is close to take-home; the net view compares
      CAT 1b cash salary less the 6% employee VNPF against the Australian figure after 2025–26 resident
      income tax and the 2% Medicare levy. Gross view compares cash salary (base + allowance, excluding
      employer VNPF/gratuity) with the Australian gross excluding 12% superannuation. NSW top of scale is
      AUD 129,536 from October 2025. Converted at {{ AUD_PER_VUV }} AUD/VUV.
    </p>
  </div>
</template>

<script>
import { scaleLinear } from '../../utils/chartUtils';
import { teacherParity, audNetFromGross, AUD_PER_VUV } from '../../utils/boardEngine';

export default {
  name: 'ParityGap',
  props: { data: { type: Object, required: true } },
  data: () => ({ W: 1000, H: 120, PAD: { l: 16, r: 16 }, mode: 'net', auGross: 130000, AUD_PER_VUV }),
  computed: {
    parity() { return teacherParity(this.data); },
    auValue() { return this.mode === 'gross' ? this.auGross : audNetFromGross(this.auGross); },
    pvisValue() { return this.mode === 'gross' ? this.parity.grossAud : this.parity.netAud; },
    barY() { return 52; },
    max() { return Math.max(this.auValue, this.pvisValue * 1.21) * 1.12; },
    x() { return scaleLinear([0, this.max], [this.PAD.l, this.W - this.PAD.r]); },
    stepMarkers() {
      return [
        { label: 'step 2', value: this.pvisValue * 1.1 },
        { label: 'steps 2+3', value: this.pvisValue * 1.21 },
      ];
    },
    ticks() {
      const out = [];
      const step = this.max > 120000 ? 25000 : 20000;
      for (let t = 0; t <= this.max; t += step) out.push(t);
      return out;
    },
    modeLabel() { return this.mode === 'gross' ? 'gross' : 'net'; },
  },
};
</script>

<style scoped>
.chart-box { position: relative; }
.chart { width: 100%; height: auto; display: block; }
.mode-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.mode-btn {
  background: none; border: 1px solid var(--ink); color: var(--ink); border-radius: 0;
  padding: 5px 12px; font-size: 11.5px; font-weight: 600; cursor: pointer;
}
.mode-btn.active { background: var(--ink); color: #fff; }
.au-input { margin-left: auto; font-size: 11px; color: var(--muted); display: flex; align-items: center; gap: 6px; }
.au-input input {
  width: 84px; padding: 3px 6px; font-size: 12.5px; border: 1px solid var(--ink); border-radius: 0;
  background: var(--paper); color: var(--ink); font-variant-numeric: tabular-nums;
}
.track { fill: var(--track); }
.bar { fill: var(--ink); }
.step-marker { stroke: var(--red); stroke-width: 1.5; stroke-dasharray: 3 3; }
.step-label { font-size: 10px; font-weight: 700; fill: var(--red); }
.au-marker { stroke: var(--ink); stroke-width: 2; }
.au-label { font-size: 11.5px; font-weight: 700; fill: var(--ink); }
.pvis-label { font-size: 11.5px; font-weight: 700; fill: var(--ink); }
.tick { font-size: 10.5px; fill: var(--muted); font-variant-numeric: tabular-nums; }
.readout { font-size: 13px; color: var(--ink); margin-top: 6px; }
.readout .big { font-size: 22px; font-weight: 800; margin-right: 6px; }
.readout .sub { color: var(--muted); font-size: 11.5px; }
.note { font-size: 10.5px; color: var(--muted); line-height: 1.55; margin: 10px 0 0; }
</style>
